import { useState } from 'react';
import { Settings, X, Trash2, RotateCcw, Download, Upload } from 'lucide-react';
import { useNavigationStore } from '../../stores/navigationStore';

export function NavigationSettings() {
  const [isOpen, setIsOpen] = useState(false);
  const {
    favorites,
    density,
    setDensity,
    clearRecentPages,
    collapseAll,
  } = useNavigationStore();

  const handleResetOnboarding = () => {
    localStorage.removeItem('nav-onboarding-completed');
    window.location.reload();
  };

  const handleExportPreferences = () => {
    const prefs = localStorage.getItem('navigation-store');
    if (prefs) {
      const blob = new Blob([prefs], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'navigation-preferences.json';
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleImportPreferences = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const content = event.target?.result as string;
          try {
            localStorage.setItem('navigation-store', content);
            window.location.reload();
          } catch (error) {
            console.error('Failed to import preferences:', error);
            alert('Failed to import preferences. Please check the file.');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition"
      >
        <Settings className="w-4 h-4" />
        <span>Navigation Settings</span>
      </button>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden shadow-sm">
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <Settings className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-semibold text-gray-700">Navigation Settings</span>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="p-1 text-gray-400 hover:text-gray-600 rounded transition"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-3 space-y-3 max-h-96 overflow-y-auto">
        {/* Density */}
        <div>
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 block">
            Visual Density
          </label>
          <div className="grid grid-cols-3 gap-2">
            {(['compact', 'normal', 'comfortable'] as const).map((d) => (
              <button
                key={d}
                onClick={() => setDensity(d)}
                className={`px-2 py-1.5 text-xs font-medium rounded transition ${
                  density === d
                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                    : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                }`}
              >
                {d.charAt(0).toUpperCase() + d.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Statistics */}
        <div>
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 block">
            Your Stats
          </label>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-600">Favorites:</span>
              <span className="font-semibold text-gray-900">{favorites.length}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div>
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 block">
            Actions
          </label>
          <div className="space-y-2">
            <button
              onClick={collapseAll}
              className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition"
            >
              <RotateCcw className="w-4 h-4 rotate-180" />
              <span>Collapse All Sections</span>
            </button>

            <button
              onClick={clearRecentPages}
              className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition"
            >
              <Trash2 className="w-4 h-4" />
              <span>Clear Recent Pages</span>
            </button>

            <button
              onClick={handleResetOnboarding}
              className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition"
            >
              <RotateCcw className="w-4 h-4" />
              <span>Reset Onboarding Tour</span>
            </button>
          </div>
        </div>

        {/* Import/Export */}
        <div>
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 block">
            Backup & Restore
          </label>
          <div className="space-y-2">
            <button
              onClick={handleExportPreferences}
              className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
            >
              <Download className="w-4 h-4" />
              <span>Export Preferences</span>
            </button>

            <button
              onClick={handleImportPreferences}
              className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition"
            >
              <Upload className="w-4 h-4" />
              <span>Import Preferences</span>
            </button>
          </div>
        </div>

        {/* Info */}
        <div className="pt-2 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Your navigation preferences are saved locally and synced to your account when logged in.
          </p>
        </div>
      </div>
    </div>
  );
}




























